import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import styled from "styled-components";
import Header from "../components/ContentSelection/Header";
import SideBar from "../components/SideBar/SideBar";
import AssetList from "../components/AssetList";
import ContentActionSideBar from "../components/ContentSelection/ContentActionSideBar";
import { actions as contentActions } from "../actions/content";
import LuciPropTypes from "../propTypes/";
import { actions as assetActions } from "../actions/asset";
import Pagination from "../components/Pagination";
import {
  Container,
  StickyHeader,
  MainContent
} from "../styled-components/Layout";
import ContentList from "../components/ContentSelection/ContentList";
import { makeGetConfigValue } from "../selectors/config";
import MediaReplaceableSidebar from "../components/Editor/MediaReplaceableSidebar";
import localize from "../util/localizeUtils";

const PaginationOuterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-content: flex-end;
`;

const ContentListContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
`;

const PaginationInnerContainer = styled.div`
  position: absolute;
  right: 1rem;
  bottom: 2rem;
  flex-direction: column;
`;

const ContentContainer = styled(Container)`
  z-index: 10000;
  background-color: ${props => props.theme.background};
`;

const RowContainer = styled(Container)`
  flex-direction: row;
  width: 100%;
`;

const MediaReplaceablesContainer = styled.div`
  background-color: ${props => props.theme.background};
  padding: 1rem;
  border-left: 1px solid grey;
  height: 100%;
  z-index: 10000;
  overflow-y: auto;
  overflow-x: hidden;
`;

const AssetBrowserContainer = styled(Container)`
  width: 100%;
`;

/**
 * Main layout for content selection.
 */
class ContentSelectionLayout extends React.PureComponent {
  UNSAFE_componentWillUpdate(nextProps) {
    const userLoggedIn = nextProps.authenticated;
    const categoriesPopulated =
      nextProps.categoriesInitialized && !nextProps.categoriesLoading;
    const contextsPopulated =
      nextProps.contextsInitialized && !nextProps.contextsLoading;
    const optionsInitialized = contextsPopulated && categoriesPopulated;
    const contentNeedUpdate = nextProps.needUpdate;
    const contentLoading = nextProps.contentLoading;

    if (
      userLoggedIn &&
      optionsInitialized &&
      !contentLoading &&
      nextProps.visible
    ) {
      Object.keys(nextProps.content).forEach(key => {
        if (nextProps.content[key].needUpdate || contentNeedUpdate) {
          this.props.getContent(
            nextProps.assetType,
            nextProps.content[key].searchType
          );
        }
      });
    }
  }

  onClose = () => {
    this.props.onClose();
  };

  render() {
    const {
      content,
      toggleFavorite,
      openOrderGraphicDialog,
      openBuildGraphicDialog,
      onConfirmAssetSelected,
      updateSelectedReplaceable,
      changePage,
      contentRequestQueued,
      reload,
      axisTrackEnabled,
      assetType,
      hideSideBar,
      visible
    } = this.props;

    return (
      <ContentContainer>
        <RowContainer>
          <AssetBrowserContainer>
            <StickyHeader>
              <Header visible={visible} />
            </StickyHeader>
            <RowContainer>
              <SideBar footerHeight="0rem">
                <ContentActionSideBar
                  openOrderGraphicDialog={openOrderGraphicDialog}
                  openBuildGraphicDialog={openBuildGraphicDialog}
                />
              </SideBar>
              <ContentListContainer>
                <ContentList
                  headerText={localize("localHeader")}
                  expandable={axisTrackEnabled}
                >
                  <MainContent>
                    <AssetList
                      assetList={content.localcontent.contentList}
                      toggleFavorite={(id, favorite) =>
                        toggleFavorite(id, favorite, "localcontent")
                      }
                      loading={
                        contentRequestQueued || content.localcontent.loading
                      }
                      onClick={res =>
                        updateSelectedReplaceable(
                          res.id,
                          res.thumbnailUrl,
                          res.provider
                        )
                      }
                      onDoubleClick={res => {
                        updateSelectedReplaceable(
                          res.id,
                          res.thumbnailUrl,
                          res.provider
                        );
                        onConfirmAssetSelected();
                      }}
                      showFilePath
                      error={content.localcontent.error}
                      reload={reload}
                    />
                  </MainContent>
                  <PaginationOuterContainer>
                    <PaginationInnerContainer>
                      <Pagination
                        totalPages={content.localcontent.totalPages}
                        currentPage={content.localcontent.pageNumber}
                        buffer={1}
                        onChange={newPage =>
                          changePage(newPage, "localcontent")
                        }
                      />
                    </PaginationInnerContainer>
                  </PaginationOuterContainer>
                </ContentList>

                {axisTrackEnabled ? (
                  <ContentList headerText="Track" expandable>
                    <MainContent>
                      <AssetList
                        assetList={content.trackcontent.contentList}
                        loading={
                          contentRequestQueued || content.trackcontent.loading
                        }
                        onClick={res =>
                          updateSelectedReplaceable(
                            res.id,
                            res.thumbnailUrl,
                            res.provider
                          )
                        }
                        onDoubleClick={res => {
                          updateSelectedReplaceable(
                            res.id,
                            res.thumbnailUrl,
                            res.provider
                          );
                          onConfirmAssetSelected();
                        }}
                        error={content.trackcontent.error}
                        reload={reload}
                      />
                    </MainContent>
                    <PaginationOuterContainer>
                      <PaginationInnerContainer>
                        <Pagination
                          totalPages={content.trackcontent.totalPages}
                          currentPage={content.trackcontent.pageNumber}
                          buffer={1}
                          onChange={newPage =>
                            changePage(newPage, "trackcontent")
                          }
                        />
                      </PaginationInnerContainer>
                    </PaginationOuterContainer>
                  </ContentList>
                ) : (
                  ""
                )}
              </ContentListContainer>
            </RowContainer>
          </AssetBrowserContainer>
          {hideSideBar ? (
            ""
          ) : (
            <Container>
              <MediaReplaceablesContainer>
                <MediaReplaceableSidebar assetType={assetType} />
              </MediaReplaceablesContainer>
            </Container>
          )}
        </RowContainer>
      </ContentContainer>
    );
  }
}

ContentSelectionLayout.defaultProps = {
  openOrderGraphicDialog: () => {},
  openBuildGraphicDialog: () => {},
  onConfirmAssetSelected: () => {},
  onClose: () => {},
  assetType: "",
  hideSideBar: false,
  visible: true
};

ContentSelectionLayout.propTypes = {
  content: LuciPropTypes.Content.isRequired,
  toggleFavorite: PropTypes.func.isRequired,
  getContent: PropTypes.func.isRequired,
  openOrderGraphicDialog: PropTypes.func,
  openBuildGraphicDialog: PropTypes.func,
  onConfirmAssetSelected: PropTypes.func,
  updateSelectedReplaceable: PropTypes.func.isRequired,
  changePage: PropTypes.func.isRequired,
  contentRequestQueued: PropTypes.bool.isRequired,
  reload: PropTypes.func.isRequired,
  axisTrackEnabled: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  assetType: PropTypes.string,
  hideSideBar: PropTypes.bool,
  visible: PropTypes.bool
};

const mapStateToProps = state => {
  const getConfigValue = makeGetConfigValue();
  return {
    content: state.content.contentList,
    userId: state.auth.user.userId,
    axisTrackEnabled:
      state.auth.axisUserProfile.services.includes("Track") && // ToDo: CHANGE THIS!!!  Use id instead when it's implemented axis side
      getConfigValue(state, "userConfig", "axisTrackContentDisplay", "show") ===
        "show" &&
      !state.content.restrictCategory, // Temporary until axis supports asset categories
    authenticated: state.auth.authenticated,
    contentRequestQueued: state.content.contentRequestQueued,
    needUpdate: state.content.needUpdate,
    contextList: state.content.contextList,
    categoryList: state.content.categoryList,
    assetType: state.content.assetType,
    categoriesInitialized: state.content.categoriesInitialized,
    contextsInitialized: state.assets.contextsInitialized,
    categoriesLoading: state.assets.categoriesLoading,
    contextsLoading: state.content.contextsLoading
  };
};

const mapActionCreators = {
  toggleFavorite: contentActions.toggleFavorite,
  getContent: contentActions.getContent,
  updateSelectedReplaceable: assetActions.updateSelectedReplaceable,
  changePage: contentActions.changePage,
  reload: contentActions.reload,
  updateReplaceableValue: assetActions.updateReplaceableValue
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(ContentSelectionLayout);



// WEBPACK FOOTER //
// ./app/layouts/ContentSelectionLayout.js